import { isMystery } from "./BURHistoryConstants";
import { appendAll, createElement, HISTORY_CSS } from "./BURHistoryDom";
import { planColumn } from "./BURHistoryGridDiff";
import { historyText } from "./BURHistoryTexts";
import { IStepperFrame } from "./BURHistoryTypes";

export interface IStepperOptions {
    frames: IStepperFrame[];
    createSymbol: (id: number) => HTMLElement;
    // animated: the frame is reached by the cascade animation, so its highlights are delayed (see game.css)
    onFrame?: (frame: IStepperFrame, animated: boolean) => void;
}

type Token = HTMLElement;

// Time one frame is shown while playing: the cascade animation (see game.css) plus time to read the wins
const PLAY_INTERVAL = 1800;
// Longer than the CSS transitions of a removed symbol
const REMOVE_DELAY = 800;

const GLYPH_PREVIOUS = "❙◀";
const GLYPH_PLAY = "▶";
const GLYPH_PAUSE = "❚❚";
const GLYPH_NEXT = "▶❙";

/**
 * One grid that steps through a spin's frames (start, each cascade's wins, final) instead of one table per frame.
 * Going to the next frame animates it: the winning symbols fade out, the ones above drop down and new ones fall in.
 * Any other jump (back, a step dot, a click on the grid, wrapping around) swaps the grid at once.
 */
export default class BURHistoryStepper {
    public readonly element: HTMLElement;

    private frames: IStepperFrame[];
    private createSymbol: (id: number) => HTMLElement;
    private onFrame?: (frame: IStepperFrame, animated: boolean) => void;
    private index = 0;
    private columns: number;
    private rows: number;
    // [column][row] of what is on the grid now
    private tokens: Token[][] = [];
    private board: HTMLElement;
    private label: HTMLElement;
    private dots: HTMLElement[] = [];
    private btnPlay: HTMLButtonElement;
    private playTimer = 0;
    private removeTimers = new Set<number>();
    private reducedMotion: boolean;

    constructor({ frames, createSymbol, onFrame }: IStepperOptions) {
        this.frames = frames;
        this.createSymbol = createSymbol;
        this.onFrame = onFrame;
        this.columns = frames[0].grid.length;
        this.rows = frames[0].grid[0]?.length || 0;
        this.reducedMotion =
            typeof window !== "undefined" &&
            !!window.matchMedia &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        this.element = createElement("div", HISTORY_CSS.stepper);
        this.label = createElement("div", HISTORY_CSS.subtitle);
        this.board = createElement("div", HISTORY_CSS.board);
        // Cells are as wide as 1 / columns of the board, so its ratio follows the grid
        this.board.style.setProperty("--columns", `${this.columns}`);
        this.board.style.setProperty("--rows", `${this.rows}`);
        // Clicking or tapping the grid goes to the next frame, like the dots: at once, and on from the last to the first
        this.board.onclick = () => {
            this.pause();
            this.go(this.index + 1, false);
        };

        this.btnPlay = this.createButton(GLYPH_PLAY, "play", () =>
            this.togglePlay()
        );
        // Under the grid: the dots, then the play controls
        appendAll(
            this.element,
            this.label,
            this.board,
            this.createDots(),
            this.createControls()
        );

        this.build(this.frames[0]);
        this.updateState(false);
    }

    public dispose(): void {
        this.pause();
        this.removeTimers.forEach((timer) => window.clearTimeout(timer));
        this.removeTimers.clear();
    }

    public pause(): void {
        if (this.playTimer) {
            window.clearInterval(this.playTimer);
            this.playTimer = 0;
        }
        this.btnPlay.textContent = GLYPH_PLAY;
        this.btnPlay.title = historyText("play");
    }

    private createControls(): HTMLElement {
        const controls = createElement("div", HISTORY_CSS.stepperControls);
        appendAll(
            controls,
            this.createButton(GLYPH_PREVIOUS, "previous", () =>
                this.go(this.index - 1)
            ),
            this.btnPlay,
            this.createButton(GLYPH_NEXT, "next", () => this.go(this.index + 1))
        );
        return controls;
    }

    // One dot per frame, to jump straight to it
    private createDots(): HTMLElement {
        const dots = createElement("div", HISTORY_CSS.stepperDots);
        this.frames.forEach((frame, index) => {
            const dot = createElement("button", HISTORY_CSS.stepperDot);
            dot.setAttribute("type", "button");
            dot.title = frame.label;
            dot.onclick = () => {
                this.pause();
                this.go(index, false);
            };
            this.dots.push(dot);
            dots.appendChild(dot);
        });
        return dots;
    }

    private createButton(
        text: string,
        title: "previous" | "play" | "next",
        onClick: () => void
    ): HTMLButtonElement {
        const button = document.createElement("button");
        button.setAttribute("type", "button");
        button.classList.add(HISTORY_CSS.stepperButton);
        button.textContent = text;
        button.title = historyText(title);
        button.onclick = () => {
            // Stepping by hand takes over from autoplay
            if (title !== "play") {
                this.pause();
            }
            onClick();
        };
        return button;
    }

    private togglePlay(): void {
        if (this.playTimer) {
            this.pause();
            return;
        }
        this.btnPlay.textContent = GLYPH_PAUSE;
        this.btnPlay.title = historyText("pause");
        // Starting from the end plays it again from the start
        if (this.index === this.frames.length - 1) {
            this.go(0, false);
        }
        this.playTimer = window.setInterval(() => {
            if (this.index >= this.frames.length - 1) {
                this.pause();
                return;
            }
            this.go(this.index + 1);
        }, PLAY_INTERVAL);
    }

    /**
     * Wraps around at both ends, like the paytable pages.
     * @param animate false to swap the grid at once; only a step forward is animated
     */
    private go(target: number, animate: boolean = true): void {
        const count = this.frames.length;
        const wrapped = (target + count) % count;
        const previous = this.index;
        this.index = wrapped;
        const animated =
            animate && wrapped === previous + 1 && !this.reducedMotion;
        if (animated) {
            this.transition(this.frames[previous], this.frames[wrapped]);
        } else {
            this.build(this.frames[wrapped]);
        }
        this.updateState(animated);
    }

    private updateState(animated: boolean): void {
        const frame = this.frames[this.index];
        this.label.textContent = frame.label;
        this.dots.forEach((dot, index) =>
            dot.classList.toggle(
                HISTORY_CSS.stepperDotActive,
                index === this.index
            )
        );
        this.onFrame?.(frame, animated);
    }

    // Swaps the whole grid
    private build(frame: IStepperFrame): void {
        const cells = document.createDocumentFragment();
        this.tokens = frame.grid.map((column, c) =>
            column.map((id, r) => this.createToken(id, c, r, cells))
        );
        this.board.textContent = "";
        this.board.appendChild(cells);
        this.markWinning(frame);
    }

    private createToken(
        id: number,
        column: number,
        row: number,
        parent: Node = this.board
    ): Token {
        const token = createElement("div", HISTORY_CSS.token);
        token.style.setProperty("--column", `${column}`);
        token.style.setProperty("--row", `${row}`);
        token.appendChild(this.createSymbol(id));
        parent.appendChild(token);
        return token;
    }

    // Highlights the winning cells of the frame, except the tokens given
    private markWinning(frame: IStepperFrame, except?: Set<Token>): void {
        this.tokens.forEach((column, c) =>
            column.forEach((token, r) => {
                if (!except?.has(token)) {
                    token.classList.toggle(
                        HISTORY_CSS.tokenWin,
                        !!frame.winning && frame.winning.has(`${c},${r}`)
                    );
                }
            })
        );
    }

    /**
     * From one frame to the next, as planned per column by planColumn. The symbols that move or appear are only
     * started here: their end position is set after a single reflow, so that the browser animates the move.
     */
    private transition(from: IStepperFrame, to: IStepperFrame): void {
        const startMoving: (() => void)[] = [];
        // The tokens created here. They are highlighted after the first render, so that the highlight is animated
        // (and delayed) for them like for the symbols that were already on the board.
        const fresh = new Set<Token>();
        const next: Token[][] = [];

        for (let column = 0; column < this.columns; column++) {
            const old = this.tokens[column];
            const plan = planColumn(
                from.grid[column],
                to.grid[column],
                (row) => !!from.winning && from.winning.has(`${column},${row}`),
                isMystery
            );

            next.push(
                plan.cells.map((cell, row) => {
                    const id = to.grid[column][row];
                    switch (cell.kind) {
                        case "keep": {
                            const token = old[cell.from];
                            token.style.setProperty("--row", `${row}`);
                            return token;
                        }
                        case "crossfade": {
                            // A mystery symbol revealing what it became fades into it, while it drops if it has to
                            const oldToken = old[cell.from];
                            oldToken.style.setProperty("--row", `${row}`);
                            this.fadeOut(
                                oldToken,
                                HISTORY_CSS.tokenCross,
                                HISTORY_CSS.tokenFade
                            );
                            const token = this.createToken(id, column, row);
                            fresh.add(token);
                            // Starts transparent, then the class is dropped so the opacity animates
                            token.classList.add(
                                HISTORY_CSS.tokenCross,
                                HISTORY_CSS.tokenFade
                            );
                            startMoving.push(() =>
                                token.classList.remove(HISTORY_CSS.tokenFade)
                            );
                            return token;
                        }
                        default: {
                            // New symbols start above the board, in the order they land
                            const token = this.createToken(
                                id,
                                column,
                                row - cell.drop
                            );
                            fresh.add(token);
                            if (cell.drop) {
                                startMoving.push(() =>
                                    token.style.setProperty("--row", `${row}`)
                                );
                            }
                            return token;
                        }
                    }
                })
            );
            plan.removed.forEach((row) =>
                this.fadeOut(old[row], HISTORY_CSS.tokenOut)
            );
        }

        this.tokens = next;
        this.markWinning(to, fresh);
        if (fresh.size) {
            // The start positions are applied once for all the new symbols, instead of once for each
            this.board.getBoundingClientRect();
            startMoving.forEach((start) => start());
            this.markWinning(to);
        }
    }

    // Fades the token out, then takes it off the board
    private fadeOut(token: Token, ...classes: string[]): void {
        token.classList.add(...classes);
        const timer = window.setTimeout(() => {
            this.removeTimers.delete(timer);
            token.remove();
        }, REMOVE_DELAY);
        this.removeTimers.add(timer);
    }
}
